import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import SeoLandingLayout from "@/components/SeoLandingLayout";
import { LuxeOverline } from "@/components/LuxeOverline";
import { IMAGES, LODGES } from "@/lib/site";

const FAQS = [
  {
    q: "How much are the lodges per night?",
    a: "Heron Lodge starts from £90 per night, Kingfisher from £100 and Alpine from £120, based on two guests. Extra guests (up to four total) are £15 each per stay.",
  },
  {
    q: "Is fishing included with a lodge stay?",
    a: "Yes — every lodge booking includes free access to both private carp lakes, with one complimentary rod per person per stay. Additional rods are £6 each per day.",
  },
  {
    q: "Can I bring my dog?",
    a: "Absolutely — all three lodges are dog-friendly. Up to two well-behaved dogs are welcome (£30 for one dog, £45 for two per stay). Dogs must stay on a lead around the park.",
  },
  {
    q: "What is the minimum stay?",
    a: "Lodges have a two-night minimum stay across the season, which runs mid-March to the end of November.",
  },
  {
    q: "What time is check-in and check-out?",
    a: "Lodge check-in is from 2:00 PM (latest arrival 8:00 PM) and check-out is by 11:00 AM. A refundable £100 card hold is taken on arrival as a security deposit.",
  },
  {
    q: "Is the park really adults only?",
    a: "Yes — Walnut Lakes is a strictly adults-only park (guests aged 15 and over). It's built for peace, quiet and uninterrupted fishing.",
  },
];

const INCLUDED = [
  "Private deck a stone's throw from the fishing lake",
  "Fully-fitted kitchen & flat-screen TV",
  "Bedding, towels and linen included",
  "Free fishing on both private carp lakes",
  "Dog-friendly — well-behaved pups welcome",
  "Free on-site parking beside your lodge",
];

const LodgesLincolnshire = () => (
  <SeoLandingLayout
    seo={{
      title: "Lakeside Lodges in Lincolnshire · Adults-Only Log Cabins near Boston",
      description:
        "Three adults-only lakeside lodges in the Lincolnshire fens near Boston & Spalding. Private decks, free carp fishing, dog-friendly, from £90/night. Book direct for the best price.",
      path: "/lodges-lincolnshire",
    }}
    overline="Lodges in Lincolnshire"
    titlePre="Adults-only lakeside lodges in"
    titleEm="Lincolnshire."
    intro="Walnut Lakes is a small, family-run holiday park set across ten landscaped acres between Boston and Spalding. Three log lodges — Heron, Kingfisher and Alpine — each sit on the water's edge with their own private deck, and every stay includes free fishing on our two private carp lakes."
    heroImage={IMAGES.lodgeCabin}
    heroAlt="Lakeside log lodge with private deck at Walnut Lakes, Lincolnshire"
    faqs={FAQS}
  >
    {/* The three lodges */}
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
      <LuxeOverline>Three lodges</LuxeOverline>
      <h2 className="font-serif text-3xl md:text-4xl text-[#F5EFE6] mt-4">
        Named after the birds around us
      </h2>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
        {LODGES.map((l) => (
          <div key={l.id} className="rounded-2xl overflow-hidden bg-[#14100F] border border-white/10">
            <img src={l.image} alt={`${l.name} — lakeside lodge in Lincolnshire`} className="w-full h-52 object-cover" loading="lazy" />
            <div className="p-6">
              <h3 className="font-serif text-xl text-[#F5EFE6]">{l.name}</h3>
              <p className="text-xs uppercase tracking-[0.2em] text-[#E4B2A8] mt-1">{l.tag}</p>
              <p className="mt-4 text-sm text-[#D8CDBA]">
                From <span className="text-[#F5EFE6] font-semibold">£{l.price}</span> / night · based on 2 guests
              </p>
              <Link to="/#book" className="mt-5 inline-flex items-center gap-2 link-underline text-[#F5EFE6] text-sm" data-testid={`seo-lodge-book-${l.id}`}>
                Reserve {l.name} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </section>

    {/* What's included */}
    <section className="bg-[#14100F] border-y border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <LuxeOverline>Every stay includes</LuxeOverline>
          <h2 className="font-serif text-3xl md:text-4xl text-[#F5EFE6] mt-4">
            Lodge holidays with the fishing thrown in
          </h2>
          <ul className="mt-8 space-y-3">
            {INCLUDED.map((i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-[#D8CDBA]">
                <Check size={16} className="text-[#E4B2A8] mt-0.5 flex-shrink-0" /> {i}
              </li>
            ))}
          </ul>
        </div>
        <img
          src={IMAGES.lodgeFromOutside}
          alt="Outside view of a lakeside lodge at dusk, Walnut Lakes Holiday Park Lincolnshire"
          className="w-full h-[340px] object-cover rounded-2xl"
          loading="lazy"
        />
      </div>
    </section>

    {/* Location context */}
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
      <LuxeOverline>Where we are</LuxeOverline>
      <h2 className="font-serif text-3xl md:text-4xl text-[#F5EFE6] mt-4 max-w-2xl">
        Lodge breaks between Boston, Spalding and the Lincolnshire coast
      </h2>
      <p className="mt-6 max-w-2xl text-sm md:text-base leading-relaxed text-[#D8CDBA]">
        The park sits just off the A16/A17 roundabout near Sutterton — quiet fenland lanes on one
        side, a well-served village with shops and pubs a 15-minute walk on the other. RSPB
        Frampton Marsh is 10 minutes by car, Boston Stump 15, and the Lincolnshire coast is an
        easy day trip. Free parking is right beside your lodge.
      </p>
    </section>
  </SeoLandingLayout>
);

export default LodgesLincolnshire;
